/**
 * Hook for validating a single field
 * Useful for individual form fields that need validation
 */

import React, { useState, useCallback } from 'react'
import type { FieldValidationConfig, ValidationResult } from '../types'
import { VALIDATORS } from '../registry'

interface FieldValidationState {
  isValid: boolean
  message?: string
  isValidated: boolean
  isDirty: boolean
}

interface UseFieldValidationResult {
  validationState: FieldValidationState
  validateField: (value: string) => ValidationResult
  formatInput: (value: string) => string
  clearValidation: () => void
  setValidation: (validation: Partial<FieldValidationState>) => void
}

export const useFieldValidation = (
  fieldConfig: FieldValidationConfig
): UseFieldValidationResult => {
  const [validationState, setValidationState] = useState<FieldValidationState>({
    isValid: true,
    message: undefined,
    isValidated: false,
    isDirty: false
  })


  // Validate the field value
  const validateField = useCallback((value: string): ValidationResult => {
    // Check required first
    if (fieldConfig.required) {
      const requiredValidator = VALIDATORS.required
      const requiredResult = requiredValidator.validate(value)
      if (!requiredResult.isValid) {
        const result = requiredResult
        setValidationState({
          isValid: result.isValid,
          message: result.message,
          isValidated: true,
          isDirty: true
        })
        return result
      }
    }

    // Skip other validators if empty and not required
    if (!value.trim() && !fieldConfig.required) {
      const result = { isValid: true }
      setValidationState({
        isValid: true,
        message: undefined,
        isValidated: true,
        isDirty: true
      })
      return result
    }

    // Run through all validators
    const validators = fieldConfig.validators || []
    for (const validatorConfig of validators) {
      const validator = VALIDATORS[validatorConfig.name]
      if (validator) {
        const result = validator.validate(value, validatorConfig.options)
        if (!result.isValid) {
          const finalResult = {
            isValid: false,
            message: validatorConfig.customMessage || result.message
          }
          setValidationState({
            isValid: finalResult.isValid,
            message: finalResult.message,
            isValidated: true,
            isDirty: true
          })
          return finalResult
        }
      }
    }

    // All validations passed
    const result = { isValid: true }
    setValidationState({
      isValid: true,
      message: undefined,
      isValidated: true,
      isDirty: true
    })
    return result
  }, [fieldConfig])

  // Format input using field's validators
  const formatInput = useCallback((value: string): string => {
    if (!fieldConfig.validators || !fieldConfig.formatOnInput) {
      return value
    }

    // Find the first validator that has formatInput function
    for (const validatorConfig of fieldConfig.validators) {
      const validator = VALIDATORS[validatorConfig.name]
      if (validator?.formatInput) {
        return validator.formatInput(value)
      }
    }

    return value
  }, [fieldConfig])

  // Clear validation state
  const clearValidation = useCallback(() => {
    setValidationState({
      isValid: true,
      message: undefined,
      isValidated: false,
      isDirty: false
    })
  }, [])

  // Set validation state
  const setValidation = useCallback((validation: Partial<FieldValidationState>) => {
    setValidationState(prev => ({
      ...prev,
      ...validation
    }))
  }, [])

  return {
    validationState,
    validateField,
    formatInput,
    clearValidation,
    setValidation
  }
}
